import {BaseElementAsync, BASIS, OSkinableInit, PickInit} from "back/commons/basis";
import {Button, OButtonInit} from "back/commons/widgets/buttons";
import {ArrowScrollBox, OrientedBox} from "back/commons/widgets/scroll";
import {Area, IArea, IAreaPointer, isAreaPointer} from "lib/commons/areas";
import {JLastDatas, LASTDATAS, OLastDatasInit} from "lib/commons/lastDatas";
import {REG} from 'lib/commons/registry';
import {GFX} from 'lib/commons/utils/gfx';
import {IView, IViewContainer, IViewsContainer, OViewVisitOptions, VIEWS} from "lib/commons/views";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";

/**
 * c-tabs : container de views. Les views peuvent être déclarées en fils de c-tabs ou
 *    construites à partir d'un tableau d'objets Area.
 *
 * c-tab et c-tab-area sont des widgets internes à c-tabs pour construire les onglets.
 */

/**
 *
 * State init attributes: area-context
 * Config attributes: areas-list, last-datas, vertical, movable
 * classes : .noBorder
 */
export interface Tabs<C> {
	initialize(init?: OTabsInit<C>): this
}

/** Config d'un c-tabs. */
export interface OTabsInit<C> extends OSkinableInit, OLastDatasInit {
	areas?: IArea<C>[],
	areasContext?: C,
	vertical?: boolean,
	movable?: boolean,
	showOnDragOver?: boolean,
	selTab?: string
	/** callback notifiant un changement de statut d'overflow ds onglets. */
	cbOverflow?: (overflow: boolean) => void
	noArrowScroll?: boolean
	lastDatas?: JLDTabs
	tabSkin?: string
	tabSkinOver?: string
	/** Fond de la zone des panneaux. */
	backgroundPanels?: (tabs: Tabs<C>) => Promise<HTMLElement>
}

export interface JLDTabs extends JLastDatas {
	tab?: string
}

export class Tabs<C = {}> extends BaseElementAsync implements IViewsContainer {
	protected _areas?: IArea<C>[];
	protected _areasContext?: C;
	protected _movable: boolean;
	protected _showOnDragOver: boolean;

	get areasContext(): C {return this._areasContext}

	set areasContext(val: C) {
		this._areasContext = val;
		this.refresh();
	}

	get vertical(): boolean {return this.hasAttribute('vertical')}

	set vertical(val: boolean) {
		if (DOM.setAttrBool(this, 'vertical', val)) {
			this.refresh();
		}
	}

	protected _tabSkin?: string;
	protected _tabSkinOver?: string;

	backgroundPanels?: (tabs: Tabs<C>) => Promise<HTMLElement>;

	get areas(): IArea<C>[] {return this._areas}

	get movable(): boolean {return this._movable }

	get showOnDragOver(): boolean {return this._showOnDragOver }

	async selectTab(tab: Tab<C>, lastDatas?: JLastDatas): Promise<boolean> {
		if (tab === this._selectedTab) return true;
		if (tab == null) return false;
		if (this._selectedTab && !await this._selectedTab.hide(tab)) return false;
		this._selectedTab = tab;
		await tab.show(lastDatas);
		if (this._selectedTab !== tab) {
			//tab.show() async => race cond possible si sel en // d'un autre tab.
			await tab.hide();
			return false;
		}
		this._tabs.scrollToChild(tab);
		this._mainSlot.textContent = null; //purge du backgroundPanels
		return true;
	}

	get selectedTab(): Tab<C> {return this._selectedTab}

	async addTab(area: IArea<C>, options?: { select?: boolean, position?: number, view?: IView }) {
		if (this._areas) this._areas.push(area);
		else this._areas = [area];
		let before: Element = null;
		if (options && (typeof options.position === 'number')) {
			before = this._tabs.firstElementChild;
			let p = options.position;
			while (before && p-- > 0) before = before.nextElementSibling;
		}
		const tab = this._newTab(area, before as Tab<C>, options?.view);
		if (options && options.select) await this.selectTab(tab);
	}

	async closeTab(tab: Tab<C>, silently?: boolean, removeFromAreas?: boolean): Promise<boolean> {
		if (!await tab.closeTabView(silently)) return false;
		if (removeFromAreas && tab.area && this._areas) {
			const idx = this._areas.indexOf(tab.area);
			if (idx >= 0) this._areas.splice(idx, 1);
		}
		if (this._selectedTab === tab) {
			this._selectedTab = null;
			const nextSel = (tab.nextElementSibling || tab.previousElementSibling) as Tab<C>;
			tab.remove();
			if (nextSel) {
				await this.selectTab(nextSel);
			} else {
				this.drawBackgroundPanels();
			}
		} else {
			tab.remove();
		}
		return true;
	}

	async replaceTab(tab: Tab<C>, area: IArea<C>, silently?: boolean): Promise<boolean> {
		this._areas.push(area);
		this._newTab(area, tab.nextElementSibling as Tab<C>);
		return await this.closeTab(tab, silently, true);
	}

	async drawBackgroundPanels() {
		if (this.backgroundPanels && !this._mainSlot.hasChildNodes()) {
			const c = await this.backgroundPanels(this);
			if (c) this._mainSlot.appendChild(c);
		}
	}

	reorderTabs(deleteOthers: boolean, ...tabCodes: string[]) {
		let nextTab = this._tabs.firstElementChild as Tab<C>;
		if (!nextTab) return;
		for (const tabCode of tabCodes) {
			if (nextTab.code === tabCode) {
				nextTab = nextTab.nextElementSibling as Tab<C>;
			} else {
				const tab = this.getTabByCode(tabCode);
				if (tab) this._tabs.insertBefore(tab, nextTab);
			}
		}
		if (deleteOthers && nextTab) do {
			const next = nextTab.nextElementSibling as Tab<C>;
			this.closeTab(nextTab, true);
			nextTab = next;
		} while (nextTab);
	}

	getTabByCode(code: string): Tab<C> {
		if (code != null) for (let tab = this._tabs.firstElementChild as Tab<C>; tab; tab = tab.nextElementSibling as Tab<C>) if (tab.code === code) return tab;
		return null;
	}

	findTabs(filter: (elt: Tab<C>, idx: number, array: Tab<C>[]) => boolean): Tab<C>[] {
		return this._tabs ? Array.from(this._tabs.children).filter(filter) as Tab<C>[] : null;
	}

	hasTabs(): boolean {return this._tabs.firstElementChild != null}

	visitTabs(v: (t: Tab<C>) => void | 'stop'): null | Tab<C> {
		for (let tab = this._tabs.firstElementChild as Tab<C>; tab; tab = tab.nextElementSibling as Tab<C>) {
			if (v(tab) === 'stop') return tab;
		}
		return null;
	}

	async visitTabsAsync(v: (t: Tab<C>) => Promise<any | 'stop'>): Promise<any | Tab<C>> {
		let tab = this._tabs.firstElementChild as Tab<C>;
		while (tab) {
			const next = tab.nextElementSibling as Tab<C>;
			const r = await v(tab);
			if (r === 'stop') return tab;
			if (r !== undefined) return r;
			tab = next;
		}
		return null;
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		if (options && options.visible) {
			if (this._selectedTab) return this._selectedTab.visitViews(visitor, options);
		} else {
			for (let tab = this._tabs.firstElementChild as Tab; tab; tab = tab.nextElementSibling as Tab) {
				const r = tab.visitViews(visitor, options);
				if (r !== undefined) return r;
			}
		}
	}

	async visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		if (options && options.visible) {
			if (this._selectedTab) return this._selectedTab.visitViewsAsync(visitor, options);
		} else {
			for (let tab = this._tabs.firstElementChild as Tab; tab; tab = tab.nextElementSibling as Tab) {
				const r = await tab.visitViewsAsync(visitor, options);
				if (r !== undefined) return r;
			}
		}
	}

	findTabFromDesc(node: Node): Tab | null {
		let ch = node;
		while (ch && ch.parentNode !== this) ch = DOMSH.getFlatParentElt(ch);
		if (ch) {
			//view trouvée, on recherche son tab.
			return this.visitTabs((t) => t.view === ch ? 'stop' : undefined);
		}
		return null;
	}

	_tabs: ArrowScrollBox | OrientedBox;
	protected _selectedTab: Tab<C>;
	_mainSlot: HTMLSlotElement;

	protected async _initialize(init: OTabsInit<C>): Promise<void> {
		//initConfig
		this._tabSkin = init.tabSkin;
		this._tabSkinOver = init.tabSkinOver;
		this._areas = init.areas;
		this._areasContext = init.areasContext;
		if ('movable' in init) this._movable = init.movable;
		if ('showOnDragOver' in init) this._showOnDragOver = init.showOnDragOver;
		if ('vertical' in init) this.vertical = init.vertical;
		if (init.lastDatasKey) this.setAttribute('last-datas', init.lastDatasKey);
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initSkins(init);
		this._initTabsRoot(init);
		this._tabs.addEventListener("keydown", (ev: KeyboardEvent) => {
			switch (ev.key) {
			case 'ArrowDown':
			case 'ArrowRight': {
				const next = this._selectedTab ? this._selectedTab.nextElementSibling : this._tabs.firstElementChild;
				if (next instanceof Tab && !next.hidden && !next.disabled) {
					this.selectTab(next);
					next.focus();
				}
				break;
			}
			case 'ArrowUp':
			case 'ArrowLeft': {
				const next = this._selectedTab ? this._selectedTab.previousElementSibling : this._tabs.lastElementChild;
				if (next instanceof Tab && !next.hidden && !next.disabled) {
					this.selectTab(next);
					next.focus();
				}
				break;
			}
			}
		});

		this.backgroundPanels = init.backgroundPanels;
		this._mainSlot = <slot/> as HTMLSlotElement;

		this._initShadowRoot();

		this._initTabs();
		if (init.lastDatas && init.lastDatas.tab !== undefined) {
			if (init.lastDatas.tab !== null) {
				const t = this.getTabByCode(init.lastDatas.tab);
				if (t) {
					await this.selectTab(t, init.lastDatas); //bon tab trouvé, on transmet les lastDatas.
				} else {
					await this.selectTab(this._tabs.firstElementChild as Tab<C>);
				}
			}
		} else if (init.selTab != null) {
			await this.selectTab(this.getTabByCode(init.selTab));
		} else {
			await this.selectTab(this._tabs.firstElementChild as Tab<C>);
		}
		if (!this._selectedTab) this.drawBackgroundPanels();
	}

	buildInitFromAtts(init?: OTabsInit<C>): OTabsInit<C> {
		init = super.buildInitFromAtts(init);
		const areas = BASIS.extractAttr(this, 'areas-list');
		if (areas) init.areas = this.findReg(init).mergeLists<Area<C>>(...areas.split(' '));
		const areaCtx = BASIS.extractAttr(this, 'areas-context');
		if (areas && areaCtx == null) {
			//Une liste d'area est paramétrée en attribut, mais pas de contexte => contexte == le tabs.
			init.areasContext = this as any as C;
		} else if (areaCtx) {
			init.areasContext = this.findReg(init).getSvc<C>(areaCtx);
		}
		init.lastDatas = LASTDATAS.getLastDatas<JLDTabs>(this, this.getAttribute("last-datas"));
		init.movable = this.hasAttribute("movable");
		init.showOnDragOver = this.hasAttribute("show-on-dragover");
		return init;
	}

	protected _initSkins(init: OTabsInit<C>) {
		this.findReg(init).installSkin('c-tabs/base', this.shadowRoot);
		this._initAndInstallSkin(this.localName, init);
	}

	protected _initTabsRoot(init: OTabsInit<C>) {
		if (init.noArrowScroll) {
			this._tabs = new OrientedBox().initialize({
				reg: this.findReg(init),
				vertical: this.vertical,
			});
		} else {
			this._tabs = new ArrowScrollBox().initialize({
				reg: this.findReg(init),
				vertical: this.vertical,
				cbOverflow: init.cbOverflow
			});
			this._tabs.addEventListener('focusout', (ev: FocusEvent) => {
				//sur la perte du focus on rescroll sur le tab actif.
				if (this.selectedTab && ev.relatedTarget instanceof Node && !this._tabs.contains(ev.relatedTarget)) {
					this._tabs.scrollToChild(this.selectedTab);
				}
			});
		}
	}

	protected _initShadowRoot() {
		const sr = this.shadowRoot;
		this._tabs.id = 'tabs';
		sr.appendChild(<div id='head'>
			<slot name='tools-start'/>
			{this._tabs}
			<slot name='tools-end'/>
		</div>);
		sr.appendChild(<div id='panels'>{this._mainSlot}</div>);
	}

	protected _initTabs() {
		//Onglets en "dur"
		for (const view of this._mainSlot.assignedNodes()) {
			if (view instanceof HTMLElement) {
				const tab = new Tab<C>().initialize({
					parentViewCtn: this,
					view: view
				});
				this._initTab(tab);
				this._tabs.appendChild(tab);
				view.hidden = true;
			}
		}

		//Onglets par Area.
		if (this._areas) this._areas.forEach((area: Area<C>) => {this._newTab(area)});
	}

	_newTab(area: IArea<C>, before?: Tab<C>, view?: IView): Tab<C> {
		const tab = new Tab<C>().initialize({
			parentViewCtn: this,
			area,
			view,
			skin: this._tabSkin,
			skinOver: this._tabSkinOver
		});
		this._initTab(tab);
		this._tabs.insertBefore(tab, before);
		return tab;
	}

	_initTab(tab: Tab<C>) {
		tab.movable = this.movable;
		tab.showOnDragOver = this.showOnDragOver;
		tab.onclick = this._onClickTab;
	}

	_onClickTab(this: Tab<C>) {
		if (!this.disabled) this.parentViewCtn.selectTab(this);
	}

	buildLastDatas(p: JLastDatas) {
		const k = this.getAttribute("last-datas");
		if (k) {
			const d = p[k] = {} as JLDTabs;
			const tab = this.selectedTab;
			if (tab) {
				d.tab = tab.code;
				//on poursuit la construction dans la view courante uniquement.
				LASTDATAS.buildLastDatas(d, tab.view, true);
			} else {
				d.tab = null; //mémoire de "pas de tab sélectionné"
			}
		}
	}

	protected _refresh() {
		this._tabs.vertical = this.vertical;
		for (let tab = this._tabs.firstElementChild as Tab; tab; tab = tab.nextElementSibling as Tab) {
			tab.refresh();
		}
	}
}

/** Stylage de base. */
REG.reg.registerSkin('c-tabs/base', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		overflow: hidden;
	}

	:host([vertical]) {
		flex-direction: row;
	}

	#head {
		display: flex;
		min-height: 1.7rem;
		min-width: 0;
	}

	:host([vertical]) #head {
		flex-direction: column;
		min-height: 0;
		min-width: 1.7em;
	}

	#tabs {
		flex: 1;
		touch-action: none;
	}

	/*:host([vertical]) #tabs {*/
	/*	flex-direction: column;*/
	/*}*/

	#tabs > * {
		display: flex;
		min-height: 0;
		min-width: 0;
		align-items: center;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		text-align: center;
		padding: 0.3ex 0.6ex;
		justify-content: center;
		user-select: none;
	}

	#tabs > .moved {
		z-index: 2;
	}

	#tabs > :not(.moved) {
		transition: top 0.2s linear, left 0.2s linear;
	}

	#panels {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	::slotted([hidden]) {
		display: none;
	}
`);

/** Stylage par défaut. */
REG.reg.registerSkin('c-tabs', 1, /* language=CSS */ `
	:host(:not([vertical])) #tabs {
		margin-bottom: -1px;
	}

	:host([vertical]) #tabs {
		margin-inline-end: -1px;
	}

	:host(:not([vertical])) #tabs > * {
		min-width: var(--tab-size, 6em);
		border-bottom: solid 2px transparent;
	}

	:host([vertical]) #tabs > * {
		min-height: var(--tab-vertical-size, 2em);
		border-inline-end: solid 2px transparent;
	}

	#tabs > *:hover {
		/*box-shadow: inset 0 0 3px var(--border-color);*/
		background: var(--pressed-bgcolor);
	}

	:host(.iconic) > #tabs > * {
		min-height: 1.5em;
		width: 1.5em;
	}

	:host(:not([vertical])) #tabs > .selected {
		border-color: var(--tabl-select-color);
		color: var(--tabl-select-color);
		-webkit-text-stroke: .5px var(--tabl-select-color);
	}

	:host([vertical]) #tabs > .selected {
		border-color: var(--tabl-select-color);
		color: var(--tabl-select-color);
		-webkit-text-stroke: .5px var(--tabl-select-color);
	}

	#panels {
		border: solid 1px var(--border-color);
	}

	:host(.noBorder) #panels {
		border: none;
		border-top: solid 1px var(--border-color);
	}

	:host(.noBorder[vertical]) #panels {
		border: none;
		border-inline-start: solid 1px var(--border-color);
	}
`);

customElements.define('c-tabs', Tabs);

/**
 *
 */
export interface Tab<C> extends Button {
	/**
	 * Init programmatique obligatoire.
	 * note : tous les states du button sont imposés par le view, donc non initialisatbles ici.
	 */
	initialize(init: OTabInit<C>): this;
}

export interface OTabInit<C> extends OButtonInit, PickInit<Tab<C>, 'parentViewCtn' | 'view' | 'area'> {}

interface IMoveStartData {
	clientAxis: number,
	vertical: boolean,
	childRects: Map<Element, GFX.ILogicalRect>,
	parentRect: GFX.ILogicalRect,
	reverse: boolean,
	move: (event: MouseEvent) => any,
	moveEnd: (event: MouseEvent) => any
}

interface IShowOnDragOverData {
	x: number,
	y: number
	start: number;
}

export class Tab<C = {}> extends Button implements IViewContainer, IAreaPointer<C> {

	area: IArea<C, any>;
	parentViewCtn: Tabs<C>;
	view: IView;
	protected _moveData: IMoveStartData;
	protected _movable: (ev: MouseEvent) => void = null;
	protected _showOnDragOverData: IShowOnDragOverData;
	protected _showableOnDragOver: (ev: DragEvent) => void = null;

	public _shown: boolean = false;

	get code(): string {return VIEWS.getCode(this.view || this)}

	get label(): string {return VIEWS.getLabel(this.view || this)}

	get description(): string {return VIEWS.getDescription(this.view || this)}

	get icon(): string {return VIEWS.getIcon(this.view || this)}

	get disabled(): boolean {
		if (this._forcedDisabled !== undefined) return this._forcedDisabled;
		const view = this.view || this;
		return isAreaPointer(view) ? !view.area.isEnabled(view.areaContext) : false;
	}

	protected _forcedDisabled?: boolean | undefined

	set disabled(v: boolean | undefined) {
		this._forcedDisabled = v;
		this.refresh();
	}

	get hidden(): boolean {
		if (this._forcedHidden !== undefined) return this._forcedHidden;
		const view = this.view || this;
		return isAreaPointer(view) ? !view.area.isVisible(view.areaContext) : false;
	}

	protected _forcedHidden?: boolean | undefined

	set hidden(v: boolean | undefined) {
		this._forcedHidden = v;
		this.refresh();
	}

	get areaContext(): C {return this.parentViewCtn.areasContext as C}

	get selected(): boolean {return this._shown}

	get movable(): boolean {
		return this._movable != null;
	}

	set movable(val: boolean) {
		if (val) {
			this._movable = this._moveStart.bind(this);
			this.draggable = true;
			this.addEventListener('dragstart', this._movable);
		} else {
			this.removeEventListener('dragstart', this._movable);
			this._movable = null;
		}
	}

	get showOnDragOver(): boolean {
		return this._showableOnDragOver != null;
	}

	set showOnDragOver(val: boolean) {
		if (val) {
			this._showableOnDragOver = this._showOnDragOver.bind(this);
			this.addEventListener('dragover', this._showableOnDragOver);
		} else {
			this.removeEventListener('dragover', this._showableOnDragOver);
			this._showableOnDragOver = null;
		}
	}

	visitViews(visitor: (view: IView) => any, options?: OViewVisitOptions): any {
		if (this.view) return visitor(this.view);
	}

	visitViewsAsync(visitor: (view: IView) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return this.view ? visitor(this.view) : Promise.resolve(undefined);
	}

	needAsync(): boolean {return this.area ? this.area.needAsync(this.areaContext) : false}

	async show(lastDatas?: JLastDatas): Promise<void> {
		if (this._shown) return;
		this._shown = true;
		this._refresh();
		if (!this.view) {
			if (this.needAsync()) {
				this.view = await this.area.loadBody(this.areaContext, lastDatas) as IView;
			} else {
				this.view = this.area.buildBody(this.areaContext, lastDatas) as IView;
			}
			if (this.view) {
				VIEWS.setArea(this.view, this);
				if (this.view instanceof BaseElementAsync) await this.view.initializedAsync;
				this.parentViewCtn.appendChild(this.view);
			}
			this._refresh();
		}
		if (this._shown) VIEWS.onViewShown(this.view);
	}

	async hide(willSelect?: Tab<C>): Promise<boolean> {
		if (!this._shown) return true; //déjà masqué.
		if (!await VIEWS.canHideView(this.view)) return false;
		this._shown = false;
		this._refresh();
		VIEWS.onViewHidden(this.view);
		return true;
	}

	/** Reconstruit la view à partir de son area si elle était construite. */
	async rebuildView(): Promise<boolean> {
		const v = this.view;
		if (v) {
			if (await VIEWS.canHideViewSilently(v, true)) return false;
			if (v !== this.view) return false; // raceCond
			const newView = await this.area.loadBody(this.areaContext);
			if (v !== this.view) return false; // raceCond
			this.view.replaceWith(newView);
			this.view = newView;
		}
		return true;
	}

	/** Ne doit être appelé que par Tabs. */
	async closeTabView(silently?: boolean): Promise<boolean> {
		if (this.view) {
			//la view a été construite.
			if (!await (silently ? VIEWS.canHideViewSilently(this.view, true) : VIEWS.canHideView(this.view, true, () => {this.parentViewCtn.selectTab(this)}))) return false;
			VIEWS.onViewHidden(this.view, true);
			if (this.view) {
				this.view.remove();
				this.view = null;
			}
		}
		return true;
	}

	protected _initialize(init: OTabInit<C>) {
		//init config
		this.parentViewCtn = init.parentViewCtn;
		this.view = init.view;
		this.area = init.area;
		//init en dur de OButtonConfig
		init.role = 'tab';
		init.uiContext = 'custom';
		//
		super._initialize(init);
	}

	protected _refresh() {
		DOM.setHidden(this, this.hidden);
		DOM.setAttrBool(this, "disabled", this.disabled);
		super._refresh();
		if (this._shown) {
			this.tabIndex = 0;
			DOM.addClass(this, 'selected');
		} else {
			this.tabIndex = !this.parentViewCtn.selectedTab ? 0 : -1; //Si aucun onglet sélectionné, ils sont tous accessibles par tab.
			DOM.removeClass(this, 'selected');
		}
		if (this.description || this.parentViewCtn.vertical) DOM.setAttr(this, "title", this.description || this.label);
		if (this.view) DOM.setHidden(this.view, !this._shown);
	}

	protected _moveStart(ev: DragEvent) {
		if (ev.button == 0 && (this.previousElementSibling || this.nextElementSibling)) {
			this.style.position = 'relative';
			const vertical = this.parentViewCtn.vertical;

			// Récupération des dimensions et positions au démarrage du déplacement
			// Utilisation de LogicalRect pour gérer de manière unifiée l'horizontal et le vertical
			const rects = new Map<Element, GFX.ILogicalRect>();
			for (const childTab of this.parentElement.children as HTMLCollectionOf<HTMLElement>) {
				childTab.style.position = 'relative';
				childTab.style.left = '0';
				rects.set(childTab, GFX.getLogicalRect(childTab.getBoundingClientRect(), false, vertical));
			}

			// Calcul du sens en fonction du flex et de la direction
			const parentStyle = getComputedStyle(this.parentElement);
			const flexReverse = parentStyle.flexDirection == 'column-reverse' || parentStyle.flexDirection == 'row-reverse';
			const reverse = Math.abs(parentStyle.direction == 'rtl' ? -1 : 0) + (flexReverse ? 1 : 0) == 1;

			const move = this._move.bind(this);
			const moveEnd = this._moveEnd.bind(this);
			this._moveData = {
				clientAxis: vertical ? ev.clientY : ev.clientX,
				vertical,
				childRects: rects,
				parentRect: GFX.getLogicalRect(this.offsetParent.getBoundingClientRect(), false, vertical),
				reverse,
				move,
				moveEnd
			};

			if (ev.dataTransfer.effectAllowed === 'uninitialized') {
				ev.dataTransfer.effectAllowed = 'all';
				ev.dataTransfer.setDragImage(<span/>, 0, 0);
			}

			// Enregistrement des listeners
			window.addEventListener('dragover', move);
			this.addEventListener('dragend', moveEnd);
		}
	}

	protected _move(ev: DragEvent) {
		if (!this._moveData) return;
		const {clientAxis: startClientAxis, vertical, childRects, parentRect} = this._moveData;
		const startRect = childRects.get(this);
		const side = vertical ? 'top' : 'left';
		const clientAxis = vertical ? ev.clientY : ev.clientX;
		const clientInverseAxis = vertical ? ev.clientX : ev.clientY;

		if (clientInverseAxis < parentRect.blockStart || clientInverseAxis > parentRect.blockEnd) return;

		// Calcul du delta borné sur la largeur du parent
		// TODO lorsqu'un scroll survient lors du déplacement, ces bornes deviennent incorrectes
		const minDelta = parentRect.inlineStart - startRect.inlineStart;
		const maxDelta = parentRect.inlineEnd - startRect.inlineEnd;
		const delta = Math.min(Math.max(clientAxis - startClientAxis, minDelta), maxDelta);

		if (!this.classList.contains('moved')) {
			if (delta) {
				this.classList.add('moved');
			} else return;
		}

		// Déplacement par tranformation
		this.style.setProperty(side, `${delta}px`);
		const inlineStart = startRect.inlineStart + delta;
		const inlineEnd = startRect.inlineEnd + delta;

		// Décalage des frères
		for (const childTab of this.parentElement.children as HTMLCollectionOf<HTMLElement>) {
			if (childTab != this) {
				const childRect = childRects.get(childTab);
				const childCenter = childRect.inlineStart + childRect.inlineSize / 2;
				if (childRect.inlineStart < startRect.inlineStart) {
					childTab.style.setProperty(side, inlineStart <= childCenter ? `${startRect.inlineSize}px` : '0');
				} else {
					childTab.style.setProperty(side, inlineEnd >= childCenter ? `-${startRect.inlineSize}px` : '0');
				}
			}
		}
	}

	protected _moveEnd(ev: DragEvent) {
		const movedDatas = this._moveData;
		if (!movedDatas) return;
		this._moveData = null;

		// Désenregistrement des listeners
		window.removeEventListener('dragover', movedDatas.move);
		this.removeEventListener('dragend', movedDatas.moveEnd);

		if (!this.classList.contains('moved')) return;
		ev.preventDefault();

		const {vertical, childRects, reverse, parentRect} = movedDatas;

		const side = vertical ? 'top' : 'left';
		const startRect = childRects.get(this);
		const rect = GFX.getLogicalRect(this.getBoundingClientRect(), false, vertical);

		// Désactivation des transformations et transitions avant modification du DOM
		for (const childTab of this.parentElement.children as HTMLCollectionOf<HTMLElement>) {
			//childTab.style.position = null; non, écrase la valeur originale css
			childTab.style.setProperty(side, '');
			childTab.style.position = '';
		}

		const clientInverseAxis = vertical ? ev.clientX : ev.clientY;
		if (clientInverseAxis < parentRect.blockStart || clientInverseAxis > parentRect.blockEnd) return;

		const children = Array.from(this.parentElement.children);
		if (reverse) children.reverse();
		const index = children.indexOf(this);

		if (rect.inlineStart < startRect.inlineStart) {
			// Insertion sur un frère précédent
			const preceding = children.slice(0, index);
			for (const childTab of preceding) {
				const childRect = childRects.get(childTab);
				const childCenter = childRect.inlineStart + childRect.inlineSize / 2;
				if (rect.inlineStart <= childCenter) {
					this.parentNode.insertBefore(this, reverse ? childTab.nextElementSibling : childTab);
					break;
				}
			}
		} else {
			// Insertion sur un frère suivant
			const following = children.slice(index + 1).reverse();
			for (const childTab of following) {
				const childRect = childRects.get(childTab);
				const childCenter = childRect.inlineStart + childRect.inlineSize / 2;
				if (rect.inlineEnd >= childCenter) {
					this.parentNode.insertBefore(this, reverse ? childTab : childTab.nextElementSibling);
					break;
				}
			}
		}

		for (const childTab of this.parentElement.children as HTMLCollectionOf<HTMLElement>) {
			childTab.style.transition = '';
		}

		DOM.removeClass(this, 'moved');
	}

	async _showOnDragOver(ev: DragEvent) {
		if (this._shown) return;
		const cancel = () => {
			this.classList.remove('willBeShown');
			this._showOnDragOverData = null;
		}
		const now = Date.now();
		if (!this._showOnDragOverData || Math.abs(ev.clientX - this._showOnDragOverData.x) > 1 || Math.abs(ev.clientY - this._showOnDragOverData.y) > 1) {
			cancel();
			this._showOnDragOverData = {
				x: ev.clientX,
				y: ev.clientY,
				start: now
			};
		} else if (now - this._showOnDragOverData.start > 1000) {
			cancel();
			await this.parentViewCtn.selectTab(this);
		} else if (now - this._showOnDragOverData.start > 700) {
			this.classList.add('willBeShown');
		}
	};
}

customElements.define('c-tab', Tab);
